// @givmo/board — the dialogs about a connected account: moving a departed officer's meetings, and
// disconnecting a calendar (CBG-CAL1 slice 1d; the approved frames `dialog-move-meetings`,
// `dialog-disconnect`, `org-accounts-disconnect`).
//
// Each says what happened and what each choice does to everyone invited, and offers only acts the
// backend serves: Board's `POST …/calendar/moves` (every upcoming meeting a connection organizes, in
// one act: a cancellation from the old calendar and one new invitation to everyone invited; or
// `none`, which stops keeping them in step), then core's `DELETE /org/{org_ref}/integrations/{id}`.
//
// ⚠ A DISCONNECT NEVER LEAVES A MEETING BEHIND ON A DEAD CONNECTION. A calendar that sends upcoming
// meetings gets Board's choice FIRST (contract §9: "Disconnect is preceded, in the web, by Board's
// choice"); the disconnect runs only once every meeting moved, or was let go. A move that leaves any
// meeting needing attention stops there, with nothing disconnected.
//
// A MOVE THAT DIDN'T FINISH SAYS SO, in the dialog that ran it (CBG-FIX36 P3; design-225's frames
// p3a and p3b): how many moved, and each meeting left with one line. The disconnect's title says the
// calendar is still connected; the handover keeps its own. Read off the meetings as they read after
// the move (`moveOutcome`), never guessed from the answer's code alone. The handover a reader
// without `board_calendar:manage` runs meeting by meeting says it the same way, a refusal stopping
// none of the rest (CBG-FIX114, `moveEach`); and the dialog's refresh names every meeting it moved,
// so the page it was opened on reads its meeting, and its Invitations line, again.
//
// ⚠ WHERE BOARD IS OFF for the organization, revoking access still works (core: "Revoking access
// must never depend on Board being on"), and the dialog offers none of Board's choices and calls no
// Board route (the orchestrator's condition on the Connected accounts card): `meetings` is then
// `null`, unknown, and the plain confirmation makes no claim about meetings it couldn't read.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type BoardCalendarSendersOut,
  type BoardCalendarMovesOut,
  type OrgBoardMeetingOut,
} from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, RadioTile, RadioTileGroup } from '@givmo/design-system';
import type { BoardOperations } from '../../board-operations';
import { useBoardConnectionOperations, useBoardOperations } from '../../board-runtime';
import { useRefreshCalendar } from '../../calendar/calendar-queries';
import { calendarWords as w } from '../../calendar/calendar-words';
import { decodeMeetingCalendar } from '../meetings/calendar-decode';
import { extractMeetings } from '../meetings/meetings-decode';
import { BODY, DIALOG_TITLE, SMALL, STRONG, attentionLine, meetingWeekday } from './calendar-parts';

/** The connection a dialog is about: the fields every read of one carries. */
export interface AccountRef {
  connection_id: string;
  account_email: string;
  account_kind: string;
}

function Shell({
  label,
  testId,
  width = 448,
  children,
  onClose,
}: {
  label: string;
  testId: string;
  width?: number;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={label}
        width={width}
        className="flex flex-col gap-16"
        testId={testId}
      >
        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function Buttons({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap justify-end gap-10">{children}</div>;
}

/** The backend's own sentence for a refusal, or the shipped fixed line (the build's B8). */
export function failureLine(error: unknown): string {
  return isGivmoError(error) && error.message.trim().length > 0 ? error.message : w.failed;
}

/** Whether a move left any meeting where it was, needing attention. */
function moveIncomplete(result: BoardCalendarMovesOut | undefined): boolean {
  return (result?.results ?? []).some((row) => row.outcome === 'needs_attention');
}

/** The backend's refusal of one meeting's move that created nothing at the new sender. */
const MOVE_NOT_COMPLETED = 'board_calendar_move_not_completed';

/**
 * A refusal of the act itself, not of one meeting's move: a typed answer other than "nothing was
 * moved", which a moves answer reports per meeting.
 */
function refusesTheAct(error: unknown): boolean {
  return (
    isGivmoError(error) && error.kind === 'request' && firstErrorCode(error) !== MOVE_NOT_COMPLETED
  );
}

/**
 * The handover meeting by meeting (`POST …/meetings/{id}/invitations/move`), for a reader without
 * `board_calendar:manage`, read as one moves answer (CBG-FIX114): every meeting is asked, a refusal
 * stopping none of the rest. A meeting its answer leaves needing attention, one refused, and one
 * whose answer was lost are the answer's `needs_attention` rows, and the dialog reads where each now
 * goes out from, as it does the moves answer's. Only when every meeting met a refusal of the act
 * itself, so nothing moved, does the act fail as the backend says it, its choices still open.
 */
async function moveEach(
  operations: BoardOperations,
  orgRef: string,
  meetings: readonly OrgBoardMeetingOut[],
  destination: { send_from: SendFrom; connection_id?: string },
): Promise<BoardCalendarMovesOut> {
  const results: NonNullable<BoardCalendarMovesOut['results']> = [];
  const refusals: unknown[] = [];
  for (const meeting of meetings) {
    try {
      const { data } = await operations.officerMoveBoardMeetingInvitations({
        path: { org_ref: orgRef, meeting_id: meeting.id },
        body: destination,
        throwOnError: true,
      });
      const block = decodeMeetingCalendar(data?.calendar);
      results.push(
        block?.state === 'needs_attention'
          ? {
              meeting_id: meeting.id,
              outcome: 'needs_attention',
              needs_attention_code: block.needs_attention_code,
            }
          : { meeting_id: meeting.id, outcome: 'moved' },
      );
    } catch (error) {
      refusals.push(error);
      results.push({ meeting_id: meeting.id, outcome: 'needs_attention' });
    }
  }
  if (refusals.length > 0 && refusals.length === meetings.length && refusals.every(refusesTheAct)) {
    throw refusals[0];
  }
  return { results };
}

/** One meeting a move didn't finish, and the line under it. */
interface Unfinished {
  meeting: OrgBoardMeetingOut;
  line: string;
}

/** What a move that didn't finish says: how many moved, of how many, and each meeting left. */
interface MoveOutcome {
  moved: number;
  total: number;
  /** How many the move didn't finish, named or not. */
  left: number;
  /** Each one the dialog can name, by its day and title. */
  unfinished: Unfinished[];
}

/**
 * A moves answer with `needs_attention` rows, said (CBG-FIX36 P3). Such a row is a meeting that
 * STAYED where it was (the new calendar refused it, so nothing moved) or one that MOVED with something
 * unfinished (its old invitation's cancellation failed, the create's answer was lost, or Givmo's mail
 * partly failed), and the row's code can't always say which. So each is read off `after`, the
 * meetings as they read after the move: one still sent from `source`'s address stayed ("Not moved");
 * any other moved, and says its meeting page's own line. One the re-read doesn't hold says only the
 * line its code gives, which claims nothing about where it is. Its day and title come from the
 * re-read, else the meetings the dialog listed; one neither holds is counted, not named.
 */
function moveOutcome(
  answer: BoardCalendarMovesOut | undefined,
  source: string,
  listed: readonly OrgBoardMeetingOut[],
  after: ReadonlyMap<string, OrgBoardMeetingOut>,
): MoveOutcome {
  const rows = answer?.results ?? [];
  const named = new Map(listed.map((meeting) => [String(meeting.id), meeting] as const));
  const sourceAddress = source.trim().toLowerCase();
  const unfinished: Unfinished[] = [];
  let left = 0;
  for (const row of rows) {
    if (row.outcome !== 'needs_attention') continue;
    left += 1;
    const id = String(row.meeting_id);
    const now = after.get(id);
    const meeting = now ?? named.get(id);
    if (meeting === undefined) continue;
    const code = row.needs_attention_code ?? null;
    const block = now === undefined ? null : decodeMeetingCalendar(now.calendar);
    let line: string;
    if (block === null) {
      line =
        code === 'old_invitation_not_cancelled'
          ? w.attention.old_invitation_not_cancelled
          : w.attention.other;
    } else if (
      block.channel === 'org_calendar' &&
      (block.organizer_email ?? '').trim().toLowerCase() === sourceAddress
    ) {
      line = w.notMoved;
    } else {
      // Moved: the meeting page's line, for its code now (or the answer's, once that has cleared).
      line = attentionLine(
        block.state === 'needs_attention' ? block : { ...block, needs_attention_code: code },
      );
    }
    unfinished.push({ meeting, line });
  }
  return { moved: rows.length - left, total: rows.length, left, unfinished };
}

/**
 * After a move that didn't finish: refresh what a calendar act changes, then read where each meeting
 * now goes out from (`moveOutcome`). The meetings list `refresh` just read again IS that read; a list
 * from before the move began is read afresh (`staleTime`), and a read that fails names no position.
 */
function useUnfinishedMove(orgRef: string) {
  const queryClient = useQueryClient();
  const operations = useBoardOperations();
  const refresh = useRefreshCalendar(orgRef);
  return async (
    answer: BoardCalendarMovesOut | undefined,
    source: string,
    listed: readonly OrgBoardMeetingOut[],
    startedAt: number,
  ): Promise<MoveOutcome> => {
    await refresh(...listed.map((meeting) => meeting.id));
    let after: ReadonlyMap<string, OrgBoardMeetingOut> = new Map();
    try {
      const body = await queryClient.fetchQuery({
        ...operations.officerListBoardMeetingsOptions({ path: { org_ref: orgRef } }),
        staleTime: Date.now() - startedAt,
      });
      after = new Map(extractMeetings(body).map((meeting) => [String(meeting.id), meeting]));
    } catch {
      // Unread: each meeting left says the line its code gives.
    }
    return moveOutcome(answer, source, listed, after);
  };
}

/** A move that didn't finish, under the dialog's title: how many moved, then each meeting left. */
function UnfinishedMove({
  title,
  outcome,
  testId,
}: {
  title: string;
  outcome: MoveOutcome;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-6">
      <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
      <p className={BODY}>{w.moveUnfinished(outcome.moved, outcome.total, outcome.left)}</p>
      <ul className="mt-4 flex flex-col gap-10" data-testid={testId}>
        {outcome.unfinished.map(({ meeting, line }) => (
          <li
            key={meeting.id}
            className="flex flex-col gap-2"
            data-testid={`${testId}-${meeting.id}`}
          >
            <span className={STRONG}>
              {meetingWeekday(meeting)} · {meeting.title}
            </span>
            <span className={SMALL}>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The meetings a dialog lists: "Tue, Oct 20 · Sample quarterly meeting". */
function MeetingList({
  meetings,
  testId,
}: {
  meetings: readonly OrgBoardMeetingOut[];
  testId: string;
}) {
  return (
    <ul className="mt-4 flex flex-col gap-4" data-testid={testId}>
      {meetings.map((meeting) => (
        <li key={meeting.id} className={STRONG}>
          {meetingWeekday(meeting)} · {meeting.title}
        </li>
      ))}
    </ul>
  );
}

type SendFrom = 'my_calendar' | 'board_account' | 'givmo_email';
type Sender = NonNullable<BoardCalendarSendersOut['senders']>[number];

/** Where a move can send meetings: the senders this officer is offered, never the source itself. */
function moveTargets(senders: BoardCalendarSendersOut | null, sourceId: string): Sender[] {
  return (senders?.senders ?? []).filter(
    (sender) =>
      (sender.send_from === 'my_calendar' ||
        sender.send_from === 'board_account' ||
        sender.send_from === 'givmo_email') &&
      sender.connection_id !== sourceId,
  );
}

// ── Handover: an officer whose access ended still sends meetings ────────────────────────────────

export function MoveMeetingsDialog({
  orgRef,
  account,
  ownerName,
  meetings,
  senders,
  orgName,
  onClose,
}: {
  orgRef: string;
  /**
   * The departed officer's connection. With no `connection_id` (a reader without
   * `board_calendar:manage` can't read Board's list of connections), each meeting moves on its own
   * (`POST …/meetings/{id}/invitations/move`) — the same act, meeting by meeting (`moveEach`).
   */
  account: Omit<AccountRef, 'connection_id'> & { connection_id: string | null };
  /**
   * The departed officer's name, or null when the team list no longer names them: the title then
   * says the act ("Move meetings"), and the address appears once, in the first line (CBG-FIX116).
   */
  ownerName: string | null;
  meetings: readonly OrgBoardMeetingOut[];
  senders: BoardCalendarSendersOut | null;
  orgName: string;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCalendar(orgRef);
  const targets = moveTargets(senders, account.connection_id ?? '');
  const initial =
    targets.find((sender) => sender.send_from === 'board_account') ??
    targets.find((sender) => sender.send_from === 'my_calendar') ??
    targets[0];
  const [to, setTo] = useState<string>(initial?.send_from ?? '');
  const unfinishedMove = useUnfinishedMove(orgRef);
  const [outcome, setOutcome] = useState<MoveOutcome | null>(null);
  const move = useMutation({
    mutationFn: async (): Promise<MoveOutcome | null> => {
      const startedAt = Date.now();
      const target = targets.find((sender) => sender.send_from === to);
      const destination = {
        send_from: to as SendFrom,
        ...(target?.connection_id ? { connection_id: target.connection_id } : {}),
      };
      const { data } =
        account.connection_id === null
          ? { data: await moveEach(operations, orgRef, meetings, destination) }
          : await operations.officerMoveBoardCalendarMeetings({
              path: { org_ref: orgRef },
              body: { from_connection_id: account.connection_id, to: destination },
              throwOnError: true,
            });
      // A move that left a meeting behind says so here, as the disconnect does, instead of closing.
      return moveIncomplete(data)
        ? await unfinishedMove(data, account.account_email, meetings, startedAt)
        : null;
    },
    onSuccess: async (unfinished) => {
      if (unfinished !== null) {
        setOutcome(unfinished);
        return;
      }
      await refresh(...meetings.map((meeting) => meeting.id));
      onClose();
    },
  });
  const count = meetings.length;
  if (outcome !== null) {
    return (
      <Shell
        label={w.moveTitle(ownerName)}
        testId="calendar-move-dialog"
        width={480}
        onClose={onClose}
      >
        <UnfinishedMove
          title={w.moveTitle(ownerName)}
          outcome={outcome}
          testId="calendar-move-unfinished"
        />
        <Buttons>
          <Button appearance="saas" tone="primary" onClick={onClose} testId="calendar-move-done">
            {w.done}
          </Button>
        </Buttons>
      </Shell>
    );
  }
  return (
    <Shell
      label={w.moveTitle(ownerName)}
      testId="calendar-move-dialog"
      width={480}
      onClose={onClose}
    >
      <div className="flex flex-col gap-6">
        <Dialog.Title className={DIALOG_TITLE}>{w.moveTitle(ownerName)}</Dialog.Title>
        <p className={BODY}>{w.moveBody(ownerName, account.account_email, count)}</p>
        <MeetingList meetings={meetings} testId="calendar-move-meetings" />
      </div>
      <div className="flex flex-col gap-8">
        <span className={STRONG}>{w.moveTo}</span>
        <RadioTileGroup
          value={to}
          onValueChange={setTo}
          aria-label={w.moveTo}
          testId="calendar-move-to"
        >
          {targets.map((sender) => (
            <RadioTile
              key={sender.send_from}
              value={sender.send_from}
              label={
                sender.send_from === 'my_calendar'
                  ? w.pickMine
                  : sender.send_from === 'board_account'
                    ? w.pickBoard
                    : w.pickGivmo
              }
              description={
                sender.send_from === 'givmo_email'
                  ? w.pickGivmoDescription(orgName)
                  : (sender.organizer_email ?? '')
              }
            />
          ))}
        </RadioTileGroup>
        <p className={SMALL}>{w.moveNote}</p>
      </div>
      {move.isError ? (
        <ErrorMessage testId="calendar-move-error">{failureLine(move.error)}</ErrorMessage>
      ) : null}
      <Buttons>
        <Button appearance="saas" tone="secondary" onClick={onClose} testId="calendar-move-cancel">
          {w.cancel}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          loading={move.isPending}
          disabled={to.length === 0 || count === 0}
          onClick={() => move.mutate()}
          testId="calendar-move-confirm"
        >
          {w.moveButton(count)}
        </Button>
      </Buttons>
    </Shell>
  );
}

// ── Disconnecting a calendar ─────────────────────────────────────────────────────────────────────

export function DisconnectDialog({
  orgRef,
  account,
  own,
  ownerName,
  meetings,
  meetingCount,
  senders,
  orgName,
  onClose,
  onDisconnected,
}: {
  orgRef: string;
  account: AccountRef;
  /** The viewer's own calendar ("your calendar"). */
  own: boolean;
  /** Whose calendar it is, by name (another officer's), or null for the board account. */
  ownerName: string | null;
  /**
   * The upcoming meetings it sends; `null` when Board couldn't say (Board off for the organization,
   * or its calendar not served): the plain confirmation then claims nothing about meetings.
   */
  meetings: readonly OrgBoardMeetingOut[] | null;
  /** How many it sends, when a read counts them apart from the list (Board's connections read). */
  meetingCount?: number;
  senders: BoardCalendarSendersOut | null;
  orgName: string;
  onClose: () => void;
  onDisconnected?: () => void;
}) {
  const operations = useBoardOperations();
  const connections = useBoardConnectionOperations();
  const refresh = useRefreshCalendar(orgRef);
  const count = meetings === null ? 0 : (meetingCount ?? meetings.length);
  const sends = meetings !== null && count > 0;
  const boardTarget = moveTargets(senders, account.connection_id).find(
    (sender) => sender.send_from === 'board_account',
  );
  const givmo = (senders?.senders ?? []).some((sender) => sender.send_from === 'givmo_email');
  const [choice, setChoice] = useState<string>(boardTarget !== undefined ? 'board' : 'leave');
  const unfinishedMove = useUnfinishedMove(orgRef);
  const [outcome, setOutcome] = useState<MoveOutcome | null>(null);

  const disconnect = useMutation({
    mutationFn: async (): Promise<MoveOutcome | null> => {
      if (sends) {
        const startedAt = Date.now();
        const to =
          choice === 'board' && boardTarget !== undefined
            ? {
                send_from: 'board_account' as const,
                ...(boardTarget.connection_id ? { connection_id: boardTarget.connection_id } : {}),
              }
            : choice === 'givmo'
              ? { send_from: 'givmo_email' as const }
              : { send_from: 'none' as const };
        const { data } = await operations.officerMoveBoardCalendarMeetings({
          path: { org_ref: orgRef },
          body: { from_connection_id: account.connection_id, to },
          throwOnError: true,
        });
        // A meeting the move couldn't take stays on this calendar: disconnect nothing, and say
        // what moved and what didn't (CBG-FIX36 P3).
        if (moveIncomplete(data)) {
          return unfinishedMove(data, account.account_email, meetings ?? [], startedAt);
        }
      }
      await connections.disconnectOrgIntegration({
        path: { org_ref: orgRef, connection_id: account.connection_id },
        throwOnError: true,
      });
      return null;
    },
    onSuccess: async (unfinished) => {
      if (unfinished !== null) {
        setOutcome(unfinished);
        return;
      }
      await refresh();
      onDisconnected?.();
      onClose();
    },
  });

  if (outcome !== null) {
    const title = own ? w.stillConnected : w.stillConnectedOther;
    return (
      <Shell label={title} testId="calendar-disconnect-dialog" width={480} onClose={onClose}>
        <UnfinishedMove title={title} outcome={outcome} testId="calendar-disconnect-unfinished" />
        <Buttons>
          <Button
            appearance="saas"
            tone="primary"
            onClick={onClose}
            testId="calendar-disconnect-close"
          >
            {w.close}
          </Button>
        </Buttons>
      </Shell>
    );
  }

  const error = disconnect.isError ? (
    <ErrorMessage testId="calendar-disconnect-error">{failureLine(disconnect.error)}</ErrorMessage>
  ) : null;
  const actions = (
    <Buttons>
      <Button
        appearance="saas"
        tone="secondary"
        onClick={onClose}
        testId="calendar-disconnect-cancel"
      >
        {w.cancel}
      </Button>
      <Button
        appearance="saas"
        tone="danger"
        loading={disconnect.isPending}
        onClick={() => disconnect.mutate()}
        testId="calendar-disconnect-confirm"
      >
        {w.disconnect}
      </Button>
    </Buttons>
  );

  if (!sends || meetings === null) {
    const until = own
      ? w.ownDisconnectUntil
      : account.account_kind === 'board_account' || ownerName === null
        ? w.boardDisconnectUntil
        : w.orgDisconnectUntil(ownerName);
    const title = w.orgDisconnectTitle(account.account_email);
    return (
      <Shell label={title} testId="calendar-org-disconnect-dialog" onClose={onClose}>
        <div className="flex flex-col gap-6">
          <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
          <p className={BODY}>
            {until}
            {meetings !== null ? ` ${w.sendsNoMeetings}` : null}
          </p>
        </div>
        {error}
        {actions}
      </Shell>
    );
  }

  const title = own ? w.disconnectTitle : w.disconnectTitleOther(account.account_email);
  return (
    <Shell label={title} testId="calendar-disconnect-dialog" width={480} onClose={onClose}>
      <div className="flex flex-col gap-6">
        <Dialog.Title className={DIALOG_TITLE}>{title}</Dialog.Title>
        <p className={BODY}>{own ? w.disconnectBody(count) : w.disconnectBodyOther(count)}</p>
        <MeetingList meetings={meetings} testId="calendar-disconnect-meetings" />
      </div>
      <RadioTileGroup
        value={choice}
        onValueChange={setChoice}
        aria-label={w.disconnectGroup}
        testId="calendar-disconnect-choice"
      >
        {boardTarget !== undefined ? (
          <RadioTile
            value="board"
            label={w.disconnectMoveBoard(count)}
            description={own ? w.disconnectMoveBoardNote : w.disconnectMoveBoardNoteOther}
          />
        ) : null}
        {givmo ? (
          <RadioTile
            value="givmo"
            label={w.disconnectGivmo(count)}
            description={own ? w.disconnectGivmoNote(orgName) : w.disconnectGivmoNoteOther(orgName)}
          />
        ) : null}
        <RadioTile
          value="leave"
          label={w.disconnectLeave(count, own)}
          description={w.disconnectLeaveNote(count)}
        />
      </RadioTileGroup>
      {error}
      {actions}
    </Shell>
  );
}
